// Copyright (c) 2020-2023 Visual Intelligence LP. All rights reserved.
//
import Axios from 'axios';

import Order, { OrderDTO } from '@shared/models/order';
import { ListRequestParams } from '@shared/types/services';
import BaseService from '@core/services/base';
import { AssetDTO } from '@shared/models/asset';
import { DeliverableDTO } from '@shared/models/deliverable';
import { JobRequestDTO } from '@shared/models/job-request';
import { ORDER_FIELDS } from '@shared/constants/order';
import { ActivityFilter, REQUEST_PARAMS } from '@shared/constants/services';
import { Id } from '@shared/types/common';

export default class OrdersService extends BaseService<OrderDTO, Order> {
  static diToken = Symbol('orders-service');

  constructor() {
    super({ domainArea: 'orders', Model: Order });
  }

  // Setup a query structure with pagination, sorting, and filtering to retrieve data.
  getListQuery({ pagination, sorting, filtering, flag }: ListRequestParams) {
    return `
      {
        orders(
          ${REQUEST_PARAMS.pagination.fields.page}: ${pagination.page}, 
          ${REQUEST_PARAMS.pagination.fields.pageSize}: ${pagination.pageSize}, 
          ${REQUEST_PARAMS.filtering.fields.searchKeyword}: "${filtering?.searchKeyword || ''}",
          sort: { 
            field: ${sorting?.field}, 
            type: ${sorting?.type}
          },
          ${REQUEST_PARAMS.filtering.fields.activityFilter}: ${filtering?.activityFilter ?? ActivityFilter.all},
          ${REQUEST_PARAMS.filtering.fields.isBlocked}: ${filtering?.isBlocked ?? null},
          ${REQUEST_PARAMS.flag}: ${flag != undefined ? flag.toString() : '0'}
        ) {
          items {
            ${ORDER_FIELDS.id},
            ${ORDER_FIELDS.orderNumber},
            ${ORDER_FIELDS.orderName},
            ${ORDER_FIELDS.customerName},
            ${ORDER_FIELDS.type},
            ${ORDER_FIELDS.dueDate},
            ${ORDER_FIELDS.status},
            ${ORDER_FIELDS.notes},
            ${ORDER_FIELDS.isBlocked},
            ${ORDER_FIELDS.isActive},
            ${ORDER_FIELDS.actualDueDate}
          }
          pageInfo {
            totalCount,
            hasPreviousPage,
            hasNextPage
          }
        }
      }
    `;
  }

  // Get full order info.
  async getOrderFullInfo(id: Id) {
    const url = this.getURL(id);

    try {
      const { data } = await Axios.get(url);

      return new Order(data);
    } 
    catch (err) {
      console.log("****** Exception in getOrderFullInfo:", err);
      throw err;
    }
  }

  // Get full order info with filtering.
  async getOrderFullInfoFiltered(id: Id, params: ListRequestParams) {
    const url = this.getURL(id); // orders/id_value_here
    const filterBy = params.filtering?.byCategory !== undefined ? params.filtering?.byCategory : undefined;

    try {
      const { data } = await Axios.post(url, JSON.stringify({ filterBy: filterBy }), 
        { headers: { 'Content-Type': 'application/json' } }
      );
      //console.log("**** OrderFullFiltered:", data);

      return new Order(data);
    } 
    catch (err) {
      console.log("****** Exception in getOrderFullInfoFiletered:", err);
      throw err;
    }
  }

  // Get short order info (not in use?)
  async getOrderShortInfo(id: Id) {
    const url = this.getURL(`${id}/customer`);

    try {
      const { data } = await Axios.get(url);
      console.log("*** OrderShort:", data);
      return new Order(data);
    } 
    catch (err) {
      console.log("****** Exception in getOrderShortInfo:", err);
      throw err;
    }
  }

  // Get list of asset names and ids for use in the site filter.
  async getAssetList(orderId: Id): Promise<Array<{id: number, name: string, tag: string}>> {
    const url = this.getURL(`${orderId}/assets`);

    try {
      const { data } = await Axios.get<Array<{id: number, name: string, tag: string}>>(url);
      //console.log("*** AssetList for order:", data);
      return data;
    } 
    catch (err) {
      console.log("****** Exception in getAssetList:", err);
      throw err;
    }
  }

  // Note: none of the funcs below use async/await.

  createDeliverable(orderId: OrderDTO['id'], assetId: AssetDTO['id'], data: Partial<DeliverableDTO>) {
    const url = this.getURL(`${orderId}/assets/${assetId}`);

    return Axios.post(url, data);
  }

  deleteDeliverable(orderId: OrderDTO['id'], assetId: AssetDTO['id'], deliverableId: DeliverableDTO['id']) {
    const url = this.getURL(`${orderId}/assets/${assetId}/deliverables/${deliverableId}`);

    return Axios.delete(url);
  }

  createJobRequest(orderId: OrderDTO['id'], assetId: AssetDTO['id'], data: Partial<JobRequestDTO>) {
    const url = this.getURL(`${orderId}/assets/${assetId}/job-requests`);

    return Axios.post(url, data);
  }

  editJobRequest(jobRequestId: JobRequestDTO['id'], orderId: OrderDTO['id'], assetId: AssetDTO['id'], data: Partial<JobRequestDTO>) {
    const url = this.getURL(`${orderId}/assets/${assetId}/job-requests/${jobRequestId}`);

    return Axios.put(url, data);
  }

  deleteJobRequest(orderId: OrderDTO['id'], assetId: AssetDTO['id'], jobRequestId: JobRequestDTO['id']) {
    const url = this.getURL(`${orderId}/assets/${assetId}/job-requests/${jobRequestId}`);

    return Axios.delete(url);
  }

  addAssetToOrder = (orderId: OrderDTO['id'], data: { assetId: AssetDTO['id']; deliverables: Array<DeliverableDTO> }) => {
    const url = this.getURL(`${orderId}/assets`);

    return Axios.post(url, data);
  }

  deleteAsset = (orderId: OrderDTO['id'], assetId: AssetDTO['id']) => {
    const url = this.getURL(`${orderId}/assets/${assetId}`);

    return Axios.delete(url);
  }

  updateOrderSheet = (orderId: OrderDTO['id'], op: string, data: any) => {
    const url = this.getURL(`${orderId}/sheets?op=${op}`);

    return Axios.post(url, JSON.stringify(data));
  }
}